//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';
import { Placement } from 'tippy.js';

import ButtonPopover from '@components/molecules/button-popover/ButtonPopover';
import Popover from '@components/molecules/popover/Popover';
import { usePopover } from '@components/molecules/popover/usePopover';
import TimeZoneId from '@models/TimeZoneId';
import ReactAngularWidget, {
  NgWrapperDivProps,
} from '@react-bridge/react-angular-widget';
import { momentFromDate } from '@utils/moment';
import { ITrackableComponent } from '@utils/withTracking';

interface Props extends Partial<ITrackableComponent> {
  className?: string;
  customTarget?: React.ReactElement;
  handleDateChanged: (date: Date) => void;
  isDisabled?: boolean;
  /**
   * This should only be an instance of the NestedButton atom, to ensure
   * proper styling for the underlying Button target being used.
   */
  nestedButton?: React.ReactElement;
  maxDate?: Date;
  minDate?: Date;
  placement?: Placement;
  timeZone: TimeZoneId;
  value?: Date;
}

/**
 * This is the official wrapped version of the AngularJS Unison DatePicker.
 *
 * For explanations of all of the props above, see the Unison documentation:
 * https://s3.amazonaws.com/curalate-dev/unison/latest/guide/index.html#/components/datePicker
 */
const DatePicker = ({
  className = '',
  customTarget,
  handleDateChanged,
  isDisabled = false,
  maxDate,
  minDate,
  nestedButton,
  placement = 'bottom-start',
  timeZone,
  trackingId,
  value,
}: Props) => {
  const [connectPopover, { closePopover }] = usePopover();

  const handleDateChangeAndClose = (date: Date) => {
    closePopover();
    handleDateChanged(date);
  };

  const sharedPopoverProps = {
    connectPopover,
    content: (
      <DatePickerPanel
        handleDateChanged={handleDateChangeAndClose}
        maxDate={maxDate}
        minDate={minDate}
        timeZone={timeZone}
        value={value}
      />
    ),
    placement,
  } as const;

  if (customTarget) {
    return (
      <Popover
        shouldOpenOnHover={false}
        shouldShowArrow={false}
        target={customTarget}
        {...sharedPopoverProps}
      />
    );
  } else {
    const getValueText = () =>
      value && momentFromDate(value, timeZone).format('MMMM D, YYYY');

    return (
      <ButtonPopover
        className={className}
        isDisabled={isDisabled}
        placeholderText='Select a Date'
        rightContent={nestedButton}
        trackingId={trackingId}
        type='button'
        valueText={getValueText()}
        {...sharedPopoverProps}
      />
    );
  }
};

class DatePickerPanel extends ReactAngularWidget<
  Props & { ngWrapperDivProps?: NgWrapperDivProps }
> {
  protected html() {
    return `
      <cu-date-picker-panel
        value="value"
        time-zone="{{ timeZone }}"
        on-change="handleDateChanged($cuValue)"
        min-date="minDate"
        max-date="maxDate"
        data-tracking-id="{{ trackingId }}"
        data-testid="{{ trackingId }}"
      ></cu-date-picker-panel>
    `;
  }
}

export default DatePicker;

export const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;
